import { cn } from "@carry/ui";
import { buildGoogleMapsUrl, type OpenExternalUrl } from "../lib/map-link";
import { waypointActionLabel } from "../lib/normalize";
import type { DriverStop, StopKind } from "../lib/normalize";
import { MapLink } from "./MapLink";

export function StopCard({
  stop,
  onSelect,
  isNext = false,
  onDepart,
  onArrive,
  onComplete,
  onDelay,
  departDisabledReason,
  openExternalUrl,
  acting = false,
}: {
  stop: DriverStop;
  onSelect: (s: DriverStop) => void;
  /** 次の目的地として一覧最上部にピン留め表示する際に true を渡す */
  isNext?: boolean;
  /** 一覧から直接の出発打刻 (pending → in_transit)。未指定なら打刻ボタンを出さない */
  onDepart?: (s: DriverStop) => void;
  /** 一覧から直接の到着打刻 (in_transit → arrived)。未指定なら打刻ボタンを出さない */
  onArrive?: (s: DriverStop) => void;
  /** 一覧から直接の完了打刻 (arrived → completed) */
  onComplete?: (s: DriverStop) => void;
  /** 未完了 stop の遅延報告画面を開く */
  onDelay?: (s: DriverStop) => void;
  /** 別 stop が進行中など、出発を止める理由 */
  departDisabledReason?: string | null;
  /**
   * LIFF 内から外部ブラウザ / 地図アプリを開く関数 (`liff.openWindow({ external: true })`)。
   * 未指定なら通常の別タブ遷移にフォールバックする。
   */
  openExternalUrl?: OpenExternalUrl;
  /** 打刻 API 実行中。ボタンを無効化する */
  acting?: boolean;
}) {
  const statusConfig = {
    done: { cls: "bg-[var(--st-done-bg)] text-[var(--st-done-ink)]", label: "完了" },
    active: { cls: "bg-[var(--st-assigned-bg)] text-[var(--st-assigned-ink)]", label: "対応中" },
    pending: { cls: "bg-[var(--st-draft-bg)] text-[var(--st-draft-ink)]", label: "未着" },
    // キャンセル/スキップは完了 (緑) と区別し、赤系バッジで明示する
    cancelled: { cls: "bg-[var(--st-cancel-bg)] text-[var(--st-cancel-ink)]", label: "キャンセル" },
  }[stop.status];
  // 完了・キャンセルともに終了状態なので kind バッジは淡色化する (未対応の濃色と区別)
  const isTerminal = stop.status === "done" || stop.status === "cancelled";
  const kindConfig: Record<StopKind, { cls: string; label: string }> = {
    depot: { cls: "bg-[var(--ink)] text-white", label: "本" },
    pickup: {
      cls: "border-[1.5px] border-dashed border-[var(--ink)] bg-[var(--bg-raised)] text-[var(--ink)]",
      label: "P",
    },
    delivery: {
      cls: isTerminal ? "bg-[var(--ink-4)] text-white" : "bg-[var(--status-delivery)] text-white",
      label: "配",
    },
    collect: {
      cls: isTerminal ? "bg-[var(--ink-4)] text-white" : "bg-[var(--status-collect)] text-white",
      label: "回",
    },
    // 経由地 (#759)。集荷 (P) と同じ破線だが色を変え、配達・回収とは別物だと分かるようにする。
    waypoint: {
      cls: "border-[1.5px] border-dashed border-[var(--status-collect)] bg-[var(--bg-raised)] text-[var(--status-collect)]",
      label: "経",
    },
    return: {
      cls: "border-[1.5px] border-dashed border-[var(--ok)] bg-[var(--bg-raised)] text-[var(--ok)]",
      label: "返",
    },
  };
  const kind = kindConfig[stop.kind];
  const canOpen =
    stop.kind === "delivery" ||
    stop.kind === "collect" ||
    stop.kind === "pickup" ||
    stop.kind === "waypoint" ||
    stop.kind === "return";
  // 一覧から直接打刻できるアクション (詳細に入らず 1 タップで進める)。
  //  - pending → 出発打刻 / in_transit → 到着打刻 (詳細画面と同じ排他ルール)
  const canDepart =
    Boolean(onDepart) &&
    (stop.apiStatus === "pending" || (!stop.apiStatus && stop.status === "pending"));
  const canArrive =
    Boolean(onArrive) &&
    (stop.apiStatus === "in_transit" || (!stop.apiStatus && stop.status === "active"));
  const canComplete = Boolean(onComplete) && stop.apiStatus === "arrived";
  const canDelay =
    Boolean(onDelay) &&
    stop.status !== "done" &&
    stop.status !== "cancelled" &&
    stop.apiStatus !== "completed" &&
    stop.apiStatus !== "skipped" &&
    stop.apiStatus !== "cancelled";
  const actionLabel = canDepart
    ? "出発打刻"
    : canArrive
      ? "到着打刻"
      : canComplete
        ? stop.kind === "collect"
          ? "回収完了"
          : stop.kind === "waypoint"
            ? waypointActionLabel(stop.waypointAction)
            : "引渡し完了"
        : null;
  const actionDisabled = acting || (canDepart && Boolean(departDisabledReason));
  // Issue #611: 一覧からもナビへ出られるようにする (座標優先・住所フォールバック)。
  // 完了・キャンセル済みは目的地として用がないので出さない。
  const mapUrl = isTerminal
    ? null
    : buildGoogleMapsUrl({ lat: stop.lat, lng: stop.lng, address: stop.address });
  return (
    <div
      className={cn(
        "relative mx-1.5 mb-2 w-[calc(100%-12px)] rounded-lg bg-[var(--bg-raised)] shadow-sm",
        isNext
          ? "border-[2px] border-[var(--ok)] shadow-md"
          : stop.status === "active"
            ? "border-[1.5px] border-[var(--ok)]"
            : "border border-transparent",
        isTerminal && "opacity-60",
      )}
    >
      <button
        type="button"
        onClick={() => canOpen && onSelect(stop)}
        disabled={!canOpen}
        className={cn(
          "block w-full p-3.5 text-left",
          canOpen ? "cursor-pointer" : "cursor-default",
        )}
      >
        <div className="flex items-start gap-2.5">
          <div
            className={cn(
              "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[length:var(--fs-xs)] font-bold",
              kind.cls,
            )}
          >
            {kind.label}
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex min-w-0 flex-wrap justify-between gap-x-2">
              <span className="min-w-0 break-words text-[length:var(--fs-md)] font-semibold">
                {stop.name}
              </span>
              <span
                className={cn(
                  "num text-[length:var(--fs-sm)]",
                  stop.status === "done"
                    ? "text-[var(--st-done-ink)]"
                    : stop.status === "cancelled"
                      ? "text-[var(--st-cancel-ink)]"
                      : "text-[var(--ink)]",
                )}
              >
                {stop.actual ?? stop.time}
              </span>
            </div>
            <div className="mb-1.5 break-words text-[length:var(--fs-sm)] text-[var(--ink-3)]">
              {stop.address}
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[length:var(--fs-xs)] font-medium",
                  statusConfig.cls,
                )}
              >
                {statusConfig.label}
              </span>
              {stop.batch && (
                <span className="min-w-0 break-words text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                  · {stop.batch}
                </span>
              )}
            </div>
          </div>
          {canOpen && <span className="text-[length:var(--fs-lg)] text-[var(--ink-4)]">›</span>}
        </div>
      </button>
      {(mapUrl || actionLabel || canDelay) && (
        <div className="px-3.5 pb-3.5">
          {/* カード本体はタップで詳細に入るため、地図リンクはボタンの外に置く (button の入れ子は不可) */}
          {mapUrl && (
            <MapLink
              url={mapUrl}
              openExternalUrl={openExternalUrl}
              ariaLabel={`${stop.name}を地図で開く`}
              className={cn(
                "inline-flex min-h-11 items-center text-[length:var(--fs-sm)] font-medium text-[var(--info)] underline",
                (actionLabel || canDelay) && "mb-1",
              )}
            >
              地図で開く
            </MapLink>
          )}
          {(actionLabel || canDelay) && (
            <div
              className={cn("grid gap-2", actionLabel && canDelay ? "grid-cols-2" : "grid-cols-1")}
            >
              {canDelay && (
                <button
                  type="button"
                  onClick={() => onDelay?.(stop)}
                  disabled={acting}
                  className="rounded-lg border border-[var(--rule-soft)] py-2.5 text-[length:var(--fs-sm)] font-semibold text-[var(--ink-3)] disabled:opacity-70"
                >
                  遅延報告
                </button>
              )}
              {actionLabel && (
                <button
                  type="button"
                  onClick={() =>
                    canDepart ? onDepart?.(stop) : canArrive ? onArrive?.(stop) : onComplete?.(stop)
                  }
                  disabled={actionDisabled}
                  aria-describedby={
                    canDepart && departDisabledReason ? `depart-blocked-${stop.id}` : undefined
                  }
                  className={cn(
                    "rounded-lg py-2.5 text-[length:var(--fs-sm)] font-semibold text-white",
                    canDepart ? "bg-[var(--info)]" : "bg-[var(--ok)]",
                    actionDisabled && "cursor-not-allowed opacity-70",
                  )}
                >
                  {acting ? "送信中…" : actionLabel}
                </button>
              )}
            </div>
          )}
          {canDepart && departDisabledReason && (
            <p
              id={`depart-blocked-${stop.id}`}
              className="mt-2 text-[length:var(--fs-xs)] text-[var(--danger)]"
            >
              {departDisabledReason}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
